import React, { useEffect, useRef } from 'react';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import {
  Field,
  ImageField,
  GetStaticComponentProps,
  RichText,
  RichTextField,
  Text,
  constants,
  useComponentProps,
  useSitecoreContext,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { ComponentProps } from '../../lib/component-props';
import { AppDispatch, RootState } from '../../store/store';
import { fetchPartialSiteSettingsWithLanguageFallback } from '../../lib/megaScreener/fetchPartialSiteSettings';
import {
  MEGA_SCREENER_DEFAULT_SITE_ROOT_PATH,
  MegaScreenerPartialSiteSettingsItem,
} from '../../GraphQL/queries/megaScreenerPartialSiteSettings.query';
import { ExtenedLayoutServiceContextInterface } from 'lib/type/Site';
import {
  buildLoggedInUserData,
  buildQueryParameters,
  getThemeJson,
} from './buildMegaScreenerConfig';
import {
  createMegaScreenerEventHandler,
  subscribeToMegaScreenerEvents,
} from './megaScreenerEvents';
import { DEFAULT_SCREENER_TYPE, JOURNEY_STATUS, JOURNEY_STEP } from '../../constants';
import { MegaScreenerSitecoreConfig } from './addMegaScreenerPrescreenerMain';
import useApplyV2Theme from '../../lib/hooks/useApplyV2Theme';
import UiHeading from '../shared/Heading';
import { StMegaScreenerSection } from './MegaScreener.styles';
import { getCookie } from '../../lib/utils/enrollmentUtil';

declare global {
  interface Window {
    MegaScreener?: {
      load: (
        config: Record<string, unknown>,
        options: { target: string | HTMLElement }
      ) => { unmount: () => void; resumeSession: () => void };
    };
  }
}

const MEGA_SCREENER_SECTION_ID = 'mega-screener';
const MEGA_SCREENER_DOM_ID = 'mega-screener-widget';
const MEGA_SCREENER_MIN_HEIGHT_PX = 480;
const MEGASCREENER_SCRIPT_SRC =
  process.env.NEXT_PUBLIC_MEGASCREENER_URL ||
  'https://megascreener-mock.vercel.app/megascreener-embed.js';

type MegaScreenerProps = ComponentProps & {
  fields: {
    /** Page heading — rendered above the embed (not passed to the widget). */
    heading?: Field<string>;
    /** Page description — rendered above the embed (not passed to the widget). */
    description?: RichTextField;
    screenerTitle?: Field<string>;
    screenerDescription?: Field<string>;
    screenerConsentText?: Field<string>;
    screenerId?: Field<string>;
    primaryCondition?: Field<string>;
    isMultiTA?: Field<boolean>;
    primaryThemeColor?: Field<string>;
    secondaryThemeColor?: Field<string>;
    jsonTheme?: Field<string>;
    avatarSrc?: ImageField;
    showConsentFooter?: Field<boolean>;
    secureConfidentialText?: RichTextField;
  };
};

/**
 * MegaScreener embed mount point. Loads the vendor script only after Redux has
 * both enrollmentId and potentialParticipant_ID, matching AretiHealthScreener.
 */
const MegaScreener = (props: MegaScreenerProps): JSX.Element => {
  const theme = useTheme();
  const router = useRouter();
  const { isToapplyV2Theme } = useApplyV2Theme();
  const dispatch = useDispatch<AppDispatch>();
  const mountRef = useRef<HTMLDivElement>(null);
  const { sitecoreContext } = useSitecoreContext();
  const siteSettings: any = sitecoreContext?.siteSettings;
  const f = props?.fields;
  const enrollmentId = useSelector(
    (state: RootState) => state.potentialParticipantDetails?.enrollmentId
  );
  const guestRef = useSelector(
    (state: RootState) => state.potentialParticipantDetails?.potentialParticipant_ID
  );
  const potentialId = useSelector(
    (state: RootState) => state.potentialParticipantDetails?.potentialId
  );
  const selectedLanguage = useSelector((state: RootState) => state.selectedLanguage);
  const isLoggedIn = useSelector((state: RootState) => state.authentication?.isAuthenticated);
  const queryParams = useSelector((state: RootState) => state.queryParams);
  const profileBasicDetails = useSelector(
    (state: RootState) => state.crProfile?.profileData?.basicDetails
  );
  const screenerResponseId = useSelector((state: RootState) => state.cdpData?.screenerResponseID);
  const isSameBrowserResumeEnabled = siteSettings?.isResumeSameBrowser?.value || false;
  const preferredLanguage = getCookie('language') || 'en';

  const { primaryTASettings = [] } =
    useComponentProps<{ primaryTASettings: MegaScreenerPartialSiteSettingsItem[] }>(
      props.rendering.uid
    ) || {};

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (!enrollmentId || !guestRef || !potentialId) return undefined;
    if (
      getCookie('journeyStatus') === JOURNEY_STATUS.PARTIAL &&
      getCookie('journeyStep') === JOURNEY_STEP.SCREENER &&
      isSameBrowserResumeEnabled &&
      preferredLanguage === selectedLanguage?.languageCode &&
      !screenerResponseId
    )
      return undefined;

    const invitedSiteQueryParamName = siteSettings?.invitedSiteQueryParamName?.value;
    const invitedProtocolQueryParamName = siteSettings?.invitedProtocolQueryParamName?.value;
    const invitedSiteValue =
      (invitedSiteQueryParamName && router.query?.[invitedSiteQueryParamName]) || '';
    const invitedProtocolValue =
      (invitedProtocolQueryParamName && router.query?.[invitedProtocolQueryParamName]) || '';
    // Sitecore isMultiTA drives the default; invite params can only turn it off when Sitecore is true.
    const sitecoreIsMultiTA = f?.isMultiTA?.value ?? false;
    const isMultiTA = sitecoreIsMultiTA ? !(invitedSiteValue && invitedProtocolValue) : false;

    const sitecoreConfig: MegaScreenerSitecoreConfig = {
      screenerId: f?.screenerId?.value ? String(f.screenerId.value) : '',
      screenerType: siteSettings?.screenerType?.fields?.name?.value || DEFAULT_SCREENER_TYPE,
      tenantKey: siteSettings?.tenantKey?.value || '',
      isMultiTA,
      cohortAdditionalParameters: siteSettings?.cohortAdditionalParameters?.value || '',
    };

    const language =
      selectedLanguage?.languageCode?.split('-')[0]?.toLowerCase() ||
      sitecoreContext?.language?.split('-')[0]?.toLowerCase();
    const QueryParameters = buildQueryParameters(queryParams);
    const loggedInUserData = buildLoggedInUserData(isLoggedIn, profileBasicDetails);
    const config: Record<string, unknown> = {
      screenerTitle: f?.screenerTitle?.value,
      screenerDescription: f?.screenerDescription?.value,
      screenerConsentText: f?.screenerConsentText?.value,
      screenerId: f?.screenerId?.value,
      primaryCondition: siteSettings?.screenerPrimaryCondition?.fields?.key?.value ?? '',
      primaryTA: siteSettings?.screenerPrimaryTA?.fields?.name?.value ?? '',
      avatarSrc: f?.avatarSrc?.value?.src,
      showConsentFooter: f?.showConsentFooter?.value ?? false,
      secureConfidentialText: f?.secureConfidentialText?.value,
      tenantKey: siteSettings?.megaScreenerTenantKey?.value,
      language,
      countryCode: selectedLanguage?.countryISO2,
      isMultiTA,
      guestRef,
      enrollmentId,
      isResumeEnabled: false,
      responseId: screenerResponseId,

      isLoggedIn: isLoggedIn ?? false,
      validation: {
        emailRegex: siteSettings?.emailRegex?.fields?.value?.value,
        zipcodeRegex: siteSettings?.zipcodeRegex?.fields?.value?.value,
        firstNameRegex: siteSettings?.nameRegex?.fields?.value?.value,
        dob: {
          minAge: siteSettings?.minAge?.value,
          maxAge: siteSettings?.maxAge?.value,
        },
        resumeJourneyModal: {
          enabled: false,
        },
      },
      theme: getThemeJson(f?.jsonTheme?.value),
      ...(loggedInUserData ? { loggedInUserData } : {}),
      ...(QueryParameters ? { QueryParameters } : {}),
    };

    const megaScreenerConfig = Object.fromEntries(
      Object.entries(config).filter(
        ([, value]) => value !== undefined && value !== null && value !== ''
      )
    );
    let cancelled = false;
    let instance: { unmount: () => void; resumeSession: () => void } | null = null;

    const completionConfig = {
      pointOfSale: sitecoreContext?.site?.name as string | undefined,
      routerQuery: router.query,
      retainQueryParamList: siteSettings?.RetainQueryParamList?.value?.split('&'),
      removeQueryParamList: siteSettings?.RemoveQueryParamList?.value?.split('&'),
      resumeEnrollmentExpirationDays: siteSettings?.resumeEnrollmentExpirationDays?.value,
      navigate: async (pathname: string, query: Record<string, string | string[] | undefined>) =>
        router.push({ pathname, query }, undefined, { scroll: false }),
    };

    const handleEvent = createMegaScreenerEventHandler(
      dispatch,
      sitecoreConfig,
      completionConfig,
      primaryTASettings
    );
    const unsubscribeEvents = subscribeToMegaScreenerEvents(handleEvent);

    function mount() {
      if (cancelled || !mountRef.current || !window.MegaScreener) return;
      instance = window.MegaScreener.load(megaScreenerConfig, { target: mountRef.current });
      console.log('megascreener config', JSON.stringify(megaScreenerConfig));
      if (screenerResponseId && instance) {
        instance.resumeSession();
      }
    }

    if (window.MegaScreener) {
      mount();
    } else {
      let script = document.querySelector<HTMLScriptElement>(
        `script[src="${MEGASCREENER_SCRIPT_SRC}"]`
      );
      if (!script) {
        script = document.createElement('script');
        script.src = MEGASCREENER_SCRIPT_SRC;
        script.async = true;
        document.body.appendChild(script);
      }
      script.addEventListener('load', mount, { once: true });
    }

    return () => {
      cancelled = true;
      unsubscribeEvents();
      instance?.unmount();
    };
  }, [enrollmentId, guestRef, potentialId, screenerResponseId]);

  const headingText = f?.heading?.value;

  return (
    <StMegaScreenerSection component="section" id={MEGA_SCREENER_SECTION_ID}>
      {headingText &&
        (isToapplyV2Theme ? (
          <UiHeading size="h1" color="primary" align="left">
            {headingText}
          </UiHeading>
        ) : (
          <Typography
            variant="h1"
            component="h1"
            sx={{
              color: (theme) => theme.palette.themeColor.main,
              fontSize: '1.5rem',
              marginBottom: theme.spacing(2),
            }}
          >
            <Text field={f?.heading} />
          </Typography>
        ))}

      {f?.description && <RichText field={f.description} />}

      <div
        ref={mountRef}
        id={MEGA_SCREENER_DOM_ID}
        style={{ minHeight: MEGA_SCREENER_MIN_HEIGHT_PX }}
      />
    </StMegaScreenerSection>
  );
};

export const getStaticProps: GetStaticComponentProps = async (_rendering, layoutData) => {
  const context = layoutData?.sitecore?.context as ExtenedLayoutServiceContextInterface;
  const language = context?.language || process.env.DEFAULT_LANGUAGE || 'en';
  const siteRootPath = context?.siteRootPath || MEGA_SCREENER_DEFAULT_SITE_ROOT_PATH;
  if (process.env.JSS_MODE === constants.JSS_MODE.DISCONNECTED) {
    return null;
  }

  const primaryTASettings = await fetchPartialSiteSettingsWithLanguageFallback(
    language,
    siteRootPath
  );
  return {
    primaryTASettings,
  };
};

export default MegaScreener;
